import type { HANGMAN_GAME_STRINGS } from "@/components/tools/hangman-game";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type { TeamState } from "@/lib/tool-kit/games-organizers/hangman";

type Strings = typeof HANGMAN_GAME_STRINGS;

/** Each team's points; during play the team whose turn it is stands out. */
export default function HangmanTeams({
  teams,
  active,
  strings,
}: {
  teams: TeamState;
  active: boolean;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (teams.scores.length < 2) {
    return null;
  }
  const name = (index: number) => t(strings.teamName, { n: fmt(index + 1) });
  return (
    <div className="space-y-2">
      <ul
        aria-label={strings.scoreboardLabel}
        className="m-0 flex list-none flex-wrap justify-center gap-2 p-0"
      >
        {teams.scores.map((score, index) => {
          const turn = active && index === teams.turn;
          return (
            <li
              aria-current={turn ? "true" : undefined}
              className={`rounded-lg border px-4 py-2 text-center ${turn ? "border-primary bg-primary/15" : "border-border bg-card"}`}
              // biome-ignore lint/suspicious/noArrayIndexKey: teams are fixed positions
              key={index}
            >
              <span className="block font-medium text-foreground text-sm">
                {name(index)}
              </span>
              <span className="block font-bold text-2xl text-foreground">
                {fmt(score)}
              </span>
              <span className="block text-muted-foreground text-xs">
                {tPlural(strings.points, locale, score)}
              </span>
            </li>
          );
        })}
      </ul>
      {active ? (
        <p
          aria-live="polite"
          className="text-center font-medium text-foreground"
        >
          {t(strings.teamTurn, { team: name(teams.turn) })}
        </p>
      ) : null}
    </div>
  );
}
